// Requirement 21 — maintenance notices, the part the apps and the platform share (pure, browser-safe): the notice
// shape, its phase at a moment (upcoming / ongoing / restored), and its window written in the reader's own time zone.
// The owner creates notices on the server (`install.sh notice …`, apps/platform-server/main.ts `notice`); the public
// list is GET /v1/platform/notices. Times are stored and sent in UTC; every client converts them to its own zone.
import type { L3 } from './entitlements.ts';

export const NOTICE_SERVICES = ['sync', 'web_app', 'desktop_app', 'account', 'billing', 'website', 'client_portal', 'backups', 'ai', 'notifications', 'updates'] as const;
export type NoticeService = typeof NOTICE_SERVICES[number];
export const NOTICE_SEVERITIES = ['info', 'minor', 'major'] as const;
export type NoticeSeverity = typeof NOTICE_SEVERITIES[number];
export const NOTICE_STATUSES = ['scheduled', 'in-progress', 'completed', 'cancelled'] as const;
export type NoticeStatus = typeof NOTICE_STATUSES[number];

export interface MaintenanceNotice {
  id: string;
  /** UTC ISO instants */
  startsAt: string; endsAt: string;
  services: NoticeService[];
  severity: NoticeSeverity;
  status: NoticeStatus;
  /** what the owner wants to say, el / en / ru (en is the fallback for the other app languages) */
  message: L3;
  createdAt: string; updatedAt: string; completedAt: string | null;
}
export type NoticePhase = 'upcoming' | 'ongoing' | 'restored';
/** how long «service restored» stays visible after the owner marks the work completed */
export const RESTORED_MS = 24 * 3600_000;

/** where a notice stands at `now` (null: nothing to show — cancelled, long finished, or a scheduled window that passed) */
export function noticePhase(n: Pick<MaintenanceNotice, 'status' | 'startsAt' | 'endsAt' | 'completedAt'>, now = new Date()): NoticePhase | null {
  const t = now.getTime();
  if (n.status === 'cancelled') return null;
  if (n.status === 'completed') { const c = Date.parse(n.completedAt ?? n.endsAt); return Number.isFinite(c) && t - c <= RESTORED_MS ? 'restored' : null; }
  if (n.status === 'in-progress') return 'ongoing';
  const s = Date.parse(n.startsAt), e = Date.parse(n.endsAt);
  if (t < s) return 'upcoming';
  return t < e ? 'ongoing' : null;
}
/** the per-notice dismissal key: dismissing «upcoming» still shows «ongoing» and «restored» once each */
export const noticeKey = (n: Pick<MaintenanceNotice, 'id'>, phase: NoticePhase) => `${n.id}:${phase}`;

/** the notices to show now, ongoing first, then upcoming (soonest first), then restored */
export function visibleNotices(list: readonly MaintenanceNotice[], now = new Date(), dismissed: ReadonlySet<string> = new Set()): { notice: MaintenanceNotice; phase: NoticePhase }[] {
  const rank: Record<NoticePhase, number> = { ongoing: 0, upcoming: 1, restored: 2 };
  return list.map((notice) => ({ notice, phase: noticePhase(notice, now) })).filter((x): x is { notice: MaintenanceNotice; phase: NoticePhase } => !!x.phase && !dismissed.has(noticeKey(x.notice, x.phase)))
    .sort((a, b) => rank[a.phase] - rank[b.phase] || a.notice.startsAt.localeCompare(b.notice.startsAt));
}

/** the device's own time zone (IANA name), e.g. Asia/Nicosia */
export const deviceTimeZone = (): string => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } };

/**
 * The window in the reader's zone: «Tue 27 Oct, 02:00 – 03:30 (GMT+2)», the end without its date when both fall on the
 * same local day. `zone` is the short zone name of the start instant as the locale writes it (EET, GMT+2, EST…).
 */
export function noticeWindow(n: Pick<MaintenanceNotice, 'startsAt' | 'endsAt'>, locale: string, timeZone = deviceTimeZone()): { start: string; end: string; zone: string; sameDay: boolean; text: string } {
  const s = new Date(n.startsAt), e = new Date(n.endsAt);
  const loc = locale === 'en' ? 'en-GB' : locale;
  const day = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const full = new Intl.DateTimeFormat(loc, { timeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const time = new Intl.DateTimeFormat(loc, { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const zone = new Intl.DateTimeFormat(loc, { timeZone, timeZoneName: 'short', hour: '2-digit' }).formatToParts(s).find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
  const sameDay = day(s) === day(e);
  const start = full.format(s), end = sameDay ? time.format(e) : full.format(e);
  return { start, end, zone, sameDay, text: `${start} – ${end} (${zone})` };
}

/** a list from GET /v1/platform/notices (or the device's cached copy), checked field by field — anything malformed is dropped */
export function parseNotices(x: unknown): MaintenanceNotice[] {
  const arr = Array.isArray(x) ? x : (x as { notices?: unknown })?.notices;
  if (!Array.isArray(arr)) return [];
  const iso = (v: unknown) => typeof v === 'string' && Number.isFinite(Date.parse(v));
  return (arr as Record<string, unknown>[]).filter((n) => n && typeof n === 'object' && typeof n.id === 'string' && iso(n.startsAt) && iso(n.endsAt)
    && (NOTICE_STATUSES as readonly string[]).includes(String(n.status)) && (NOTICE_SEVERITIES as readonly string[]).includes(String(n.severity)) && Array.isArray(n.services) && !!n.message)
    .map((n) => {
      const m = n.message as Partial<L3>;
      return { id: String(n.id), startsAt: String(n.startsAt), endsAt: String(n.endsAt), status: n.status as NoticeStatus, severity: n.severity as NoticeSeverity,
        services: (n.services as unknown[]).filter((s): s is NoticeService => (NOTICE_SERVICES as readonly string[]).includes(String(s))),
        message: { el: String(m.el ?? m.en ?? ''), en: String(m.en ?? m.el ?? ''), ru: String(m.ru ?? m.en ?? '') },
        createdAt: String(n.createdAt ?? n.startsAt), updatedAt: String(n.updatedAt ?? n.startsAt), completedAt: iso(n.completedAt) ? String(n.completedAt) : null };
    }).slice(0, 50);
}
